/**
 * /support — copyable support email + contact form.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Check, Send, LifeBuoy } from 'lucide-react';
import { useTheme } from '../lib/theme';
import { submitSupportTicket, getSupportConfig } from '../lib/api';

const SUPPORT_EMAIL = 'support@clasifi.ca';

export default function SupportPage() {
  const { colors } = useTheme();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getSupportConfig().catch(() => null);
  }, []);

  const handleCopy = useCallback(() => {
    navigator.clipboard?.writeText(SUPPORT_EMAIL);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const resp = await submitSupportTicket({
        subject: subject.trim(),
        message: message.trim(),
        email: email.trim(),
      });
      setResult(resp);
    } catch (err) {
      setError(err?.detail || 'Something went wrong — please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  }, [subject, message, email, submitting]);

  const canSubmit = subject.trim().length >= 2 && message.trim().length >= 2 && /\S+@\S+\.\S+/.test(email);

  if (result) {
    return (
      <div className="max-w-2xl mx-auto p-8">
        <div className="rounded-xl border p-8 text-center"
          data-testid="support-thanks"
          style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
            style={{ backgroundColor: `${colors.accent}22` }}>
            <Check size={28} style={{ color: colors.accent }} />
          </div>
          <h2 className="text-xl font-bold" style={{ color: colors.text }}>Message received</h2>
          <p className="text-sm mt-2" style={{ color: colors.textMuted }}>
            Ticket <span className="font-mono text-xs px-1.5 py-0.5 rounded"
              style={{ backgroundColor: colors.bg, color: colors.text }}>{result.ticket_id}</span>. {"We'll be in touch shortly."}
          </p>
          <p className="text-xs mt-3" style={{ color: colors.textMuted }}>
            {result.email_status === 'sent'
              ? 'A confirmation has been emailed to our team.'
              : 'Your message is saved. If you don\'t hear back within 24h, please email us directly at support@clasifi.ca.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 lg:p-10">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${colors.accent}22` }}>
          <LifeBuoy size={22} style={{ color: colors.accent }} />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: colors.text }}>Contact Support</h1>
          <p className="text-sm" style={{ color: colors.textMuted }}>We usually reply within a business day.</p>
        </div>
      </div>

      {/* Copyable email */}
      <div
        data-testid="support-email-card"
        className="flex items-center gap-3 rounded-xl border p-4 mb-6"
        style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: colors.textMuted }}>Email us directly</p>
          <p className="text-sm font-mono mt-0.5" style={{ color: colors.text }}>{SUPPORT_EMAIL}</p>
        </div>
        <button
          data-testid="support-copy-email"
          onClick={handleCopy}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold"
          style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }}>
          {copied ? <Check size={12} style={{ color: colors.accent }} /> : <Copy size={12} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}
        data-testid="support-form"
        className="rounded-xl border p-5 space-y-4"
        style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
        <Field label="Your email" colors={colors}>
          <input
            type="email"
            data-testid="support-input-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="w-full px-3 py-2 rounded-lg text-sm outline-none border"
            style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}
            required />
        </Field>
        <Field label="Subject" colors={colors}>
          <input
            type="text"
            data-testid="support-input-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="What's this about?"
            className="w-full px-3 py-2 rounded-lg text-sm outline-none border"
            style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}
            required />
        </Field>
        <Field label="Message" colors={colors}>
          <textarea
            data-testid="support-input-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={6}
            placeholder="Tell us what's going on — the more detail, the faster we can help."
            className="w-full px-3 py-2 rounded-lg text-sm outline-none border resize-y"
            style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border, minHeight: 120 }}
            required />
        </Field>

        {error ? (
          <p className="text-xs" style={{ color: colors.error || '#EF4444' }}>{error}</p>
        ) : null}

        <button
          type="submit"
          data-testid="support-submit"
          disabled={!canSubmit || submitting}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-sm font-bold disabled:opacity-40"
          style={{ backgroundColor: colors.accent }}>
          <Send size={14} />
          {submitting ? 'Sending…' : 'Send message'}
        </button>
      </form>
    </div>
  );
}


function Field({ label, colors, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider font-bold mb-1 block"
        style={{ color: colors.textMuted }}>{label}</span>
      {children}
    </label>
  );
}
